// src/pages/payroll/PayrollSheet.jsx
// 급여명세서 (1단계) — 연월 선택 · insurance 명단 불러오기 · 전월 매핑 불러오기
//   · 시급등급 선택(시급 스냅샷) · 행 추가/삭제 · 저장/불러오기
//   ※ 수당은 숫자칸. 주별 입력 모달·함수 생성·엑셀 export는 다음 단계.
import React, { useEffect, useState } from "react";
import useBackdropClose from "../../hooks/useBackdropClose";
import api from "../../api";

const num = (v) => { const n = Number(String(v ?? "0").replace(/[, 원]/g, "")); return Number.isFinite(n) ? n : 0; };
const fmt = (n) => Number(n || 0).toLocaleString();
const pkey = (name, birth) => `${name}|${birth}`;
const fmtYm = (s) => (s && s.length >= 7 ? `${s.slice(0, 4)}년 ${+s.slice(5, 7)}월` : s);

// 그 달을 월~일 기준 주차로 분할 → [{label, from, to}] (ym: "2026-05")
const weeksOfMonth = (ym) => {
  if (!ym) return [];
  const [y, m] = ym.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const last = new Date(y, m, 0);
  const weeks = [];
  let cur = new Date(first);
  // 첫 주의 월요일로 back (월=1 … 일=0 → 일요일은 6칸 뒤로)
  const mondayOffset = (d) => (d.getDay() === 0 ? 6 : d.getDay() - 1);
  cur.setDate(cur.getDate() - mondayOffset(cur));
  let wn = 1;
  while (cur <= last) {
    const ws = new Date(cur);
    const we = new Date(cur); we.setDate(we.getDate() + 6);
    // 이 주가 이번 달과 겹치면 추가
    const showFrom = ws < first ? first : ws;
    const showTo = we > last ? last : we;
    if (we >= first && ws <= last) {
      weeks.push({
        label: `${wn}주차 (${showFrom.getMonth() + 1}/${showFrom.getDate()}~${showTo.getMonth() + 1}/${showTo.getDate()})`,
        idx: wn - 1,
      });
      wn++;
    }
    cur.setDate(cur.getDate() + 7);
  }
  return weeks;
};

// 수당 금액 계산: ROUNDDOWN(base * (1.5*sum15 + 1*sum10), -1)
const calcAllowance = (base, arr15, arr10, mult15 = 1.5, mult10 = 1) => {
  const sum = (a) => (a || []).reduce((s, v) => s + num(v), 0);
  const won = base * (mult15 * sum(arr15) + mult10 * sum(arr10));
  return Math.floor(won / 10) * 10;   // 10원 미만 절사
};

// 시간외/휴일 base 시급 (반장수당 통상임금 산입)
const baseHourly = (row) => {
  const h = num(row.hourly), lead = num(row.leader_allow), wh = num(row.work_hours) || 209;
  if (lead > 0) {
    const basic = Math.floor((h * wh) / 10) * 10;   // 기본급(ROUNDDOWN)
    return (basic + lead) / wh;                      // (기본급+반장)/소정시간
  }
  return h;
};


const NOW = new Date();
const YEARS = [];
for (let y = NOW.getFullYear() + 1; y >= 2026; y--) YEARS.push(y);
const MONTHS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));

// ✅ 본사 소속 판정 — 정액급식비(비공제)는 본사 행에만 입력한다.
// worksite는 choices 없는 자유 문자열이고 insurance의 소속명(EmployeeUnit.affiliation)이
// 그대로 들어오므로, 공백 변형만 흡수하고 정확히 "본사"인지로 본다.
const HEAD_OFFICE = "본사";
const isHeadOffice = (row) => String(row?.worksite || "").trim() === HEAD_OFFICE;

// ══════════════════════════════════════════════════════════════════
// 중소기업 취업자 소득세 감면 (조특법 §30)
//   90 = 청년 / 70 = 그 외 대상(60세 이상·장애인·경력단절여성) / 0 = 미적용
//
// ⚠️ 감면은 기간이 끝나면 멈춰야 한다. 실제 본사 급여내역서(2026-07)에서 "감면
// 신청완료"로 표기된 5명 중 1명은 감면기간이 이미 지나 감면이 적용돼 있지 않았다
// — 감면율만 들고 있으면 만료 후에도 계속 깎여 과소 원천징수가 된다. 그래서
// 만료월(YYYY-MM)을 같이 저장하고 급여월이 그 달을 지나면 0%로 본다.
// ══════════════════════════════════════════════════════════════════

// 조특법 30조 감면 적용 소속 — 본사: 기존부터, 종합환경: 2026-08부터
// (2026-08-24 확정, 7월 이전 소급 없음)
//
// 값은 "적용 시작 연월(YYYY-MM)"이고 ""는 시작월 제한 없음(= 처음부터)이다.
// 단순 소속 집합이 아니라 시작월을 함께 두는 이유: 종합환경은 2026-07 명세서까지
// 감면이 없고 2026-08부터 생긴다. 집합만 두면 7월 시트를 다시 열어 [수정 완료]나
// [계산]을 누르는 순간 그 달까지 소급 적용돼 저장된 세액이 조용히 바뀐다
// (아래 taxFromLookup → reliefRateFor 경로가 급여월과 무관해지므로).
// 시작월을 두면 판정이 급여월을 보고 스스로 끊어, 7월은 재계산해도 감면 0%다.
//
// ⚠️ 이 표는 감면 전용이다. 정액급식비(meal_allow)·본사 월급제 항목(base_pay 등)은
// 여전히 본사 전용이라 isHeadOffice로 판정한다 — 같이 묶지 말 것.
const TAX_RELIEF_WORKSITES = {
  "본사": "",             // 기존부터 적용 (시작월 제한 없음)
  "종합환경": "2026-08",  // 2026년 8월분 명세서부터
};

// 그 급여월(targetYm)에 이 행의 소속이 감면 대상인지. 연월이 "YYYY-MM" 고정폭이라
// 문자열 비교로 연·월 비교가 정확히 된다.
const isReliefWorksite = (row, targetYm) => {
  const start = TAX_RELIEF_WORKSITES[String(row?.worksite || "").trim()];
  if (start === undefined) return false;   // 표에 없는 소속 = 미적용
  if (!start) return true;                 // 시작월 제한 없음 = 상시 적용(본사)
  return !!targetYm && targetYm >= start;  // 시작월 "당월부터" 적용
};

// 비활성 셀에 왜 못 쓰는지 — "본사만"이 아니게 됐으므로 이유를 나눠 보여준다.
// (종합환경 7월 화면에서 "본사 소속만 입력합니다"가 뜨면 8월엔 왜 되는지 설명이 안 된다)
const reliefOffReason = (row) => {
  const start = TAX_RELIEF_WORKSITES[String(row?.worksite || "").trim()];
  if (start === undefined) return "감면 적용 소속이 아닙니다";
  return `${String(row?.worksite || "").trim()} — ${start.slice(0, 4)}년 ${+start.slice(5, 7)}월분부터 적용합니다`;
};

const TAX_RELIEF_RATES = [0, 70, 90];

// 그 급여월(targetYm)에 실제로 적용될 감면율. 만료월 "당월까지" 적용이라 초과분만
// 끊는다("2026-07" > "2026-07"은 false → 적용, "2026-08" > "2026-07"은 true → 0).
const reliefRateFor = (row, targetYm) => {
  if (!isReliefWorksite(row, targetYm)) return 0;
  const rate = num(row?.tax_relief_rate);
  if (rate <= 0) return 0;
  const until = String(row?.tax_relief_until || "").trim();
  if (!until) return rate;                 // 기한 미입력 = 무기한(저장 시 경고로 안내)
  return targetYm && targetYm > until ? 0 : rate;
};

// 감면율은 있는데 기간이 지나 이번 달엔 적용 안 되는 상태(화면에 "만료"로 표시)
const isReliefExpired = (row, targetYm) =>
  isReliefWorksite(row, targetYm) && num(row?.tax_relief_rate) > 0 && reliefRateFor(row, targetYm) === 0;

// 감면 소득세 = ROUNDDOWN(간이세액 × (1-rate/100), -1)
// 지방소득세 = ROUNDDOWN(감면 소득세 × 0.1, -1)   ← 감면액 기준으로 같이 내려간다
// ⚠️ (1 - rate/100)로 쓰면 부동소수점 오차가 난다(1-0.9 = 0.09999…라 간이세액
// 100,000원이 10,000원이 아니라 9,990원이 됨). 정수끼리 곱해서 나누는 형태 유지.
// 지방세는 백엔드 calc_local_tax(tax // 100 * 10)와 같은 식이다.
const applyTaxRelief = (baseTax, rate) => {
  const income_tax = Math.floor((num(baseTax) * (100 - num(rate))) / 100 / 10) * 10;
  return { income_tax, local_tax: Math.floor(income_tax / 100) * 10 };
};

// 새 빈 행
const emptyRow = (seq) => ({
  seq, name: "", birth: "", worksite: "", job: "", worker_type: "일반",
  wage_grade: "", hourly: 0, work_hours: 209, leader_allow: 0, annual_allow: 0,
  meal_allow: 0,
  // 본사(월급제) 전용 — 양식 D~H열. 현장 소속에서는 항상 0으로 남는다.
  base_pay: 0, special_allow: 0, position_allow: 0, overtime_allow: 0, family_allow: 0,
  overtime: {}, night: {}, holiday: {},
  health: 0, care: 0, pension: 0, employ: 0, employer: 0, injury: 0,
  income_tax: 0, local_tax: 0, tax_done: false, note: "", work_days: "",
  tax_relief_rate: 0, tax_relief_until: "",
  annual_leave_days: "", official_leave_days: "", actual_work_hours: "",
});
export default function PayrollSheet() {
  const allowBackdrop = useBackdropClose(() => setModalIdx(null));
  const mapBackdrop = useBackdropClose(() => setShowMapModal(false));

  const [ym, setYm] = useState("");
  const [rows, setRows] = useState([]);
  const [grades, setGrades] = useState([]);     // [{grade, hourly}] 그 해 시급등급
  const [months, setMonths] = useState([]);     // 저장된 명세서 연월
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [filter, setFilter] = useState("전체");   // ✅ 소속 필터

  const [modalIdx, setModalIdx] = useState(null);   // ✅ 수당입력 모달 대상 행 인덱스

  const [showMapModal, setShowMapModal] = useState(false);   // 매핑 설정 모달
  const [pickedMap, setPickedMap] = useState("");            // 가져온 db 라벨용 (선택된 월)
  const [editIdx, setEditIdx] = useState(null);              // 편집 중인 행 인덱스 (null=전부 잠금)
  const [locked, setLocked] = useState(false);               // ✅ 데이터 완료(잠금)



  const year = ym ? num(ym.slice(0, 4)) : NOW.getFullYear();

  // ✅ 명단에서 소속(근무지) 목록 추출 + 현재 필터로 보이는 행
  const sites = Array.from(new Set(rows.map((r) => r.worksite).filter(Boolean)));
  const visibleRows = filter === "전체" ? rows : rows.filter((r) => r.worksite === filter);

  // ✅ 매핑 완료 여부 — 시급등급이 채워진 행이 하나라도 있으면 완료
  const isMapped = rows.some((r) => String(r.wage_grade || "").trim() !== "");


  // ── 로더 ──
  const loadGrades = async (y) => {
    try {
      const r = await api.get("/payroll/wage-rates/", { params: { year: y } });
      setGrades(Array.isArray(r.data) ? r.data : []);
    } catch { setGrades([]); }
  };
  const loadMonths = async () => {
    try { const r = await api.get("/payroll/sheet/months/"); setMonths(Array.isArray(r.data) ? r.data : []); }
    catch { setMonths([]); }
  };


  const loadSheet = async (t = ym) => {
    if (!t) return;
    try {
      const r = await api.get("/payroll/sheet/", { params: { ym: t } });
      const list = r.data?.rows || [];

      if (list.length) {
        setRows(list.map((x, i) => recalcRow({ ...emptyRow(i + 1), ...x, tax_done: true })));
        const isLocked = !!r.data?.locked;
        setLocked(isLocked);   // ✅ 잠금 상태 반영
        setMsg(`${fmtYm(t)} 저장된 명세서를 불러왔습니다. (${list.length}명)`);

      } else {
        setLocked(false);   // ✅ 명단만 있는 상태는 잠금 해제
        // 저장된 명세서가 없으면 사회보험 명단 자동 연동
        await importRoster(t);
      }
    } catch { setRows([]); }
  };



  useEffect(() => { loadMonths(); }, []);
  useEffect(() => { if (year) loadGrades(year); }, [year]);
  useEffect(() => { if (ym) loadSheet(ym); /* eslint-disable-next-line */ }, [ym]);

  // Ctrl+S 저장 단축키
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (!busy && ym && rows.length) save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, ym, rows]);





  // 등급 → 시급 스냅샷
  const gradeHourly = (grade) => {
    const g = grades.find((x) => x.grade === grade);
    return g ? g.hourly : 0;
  };

  // 한 행의 수당 3종 금액 재계산 (overtime/night/holiday JSON → 금액)
  const recalcRow = (row) => {
    const base = baseHourly(row);
    const ot = calcAllowance(base, row.overtime?.x15, row.overtime?.x10, 1.5, 1);
    const ni = calcAllowance(base, row.night?.x05, [], 0.5, 0);
    const ho = calcAllowance(base, row.holiday?.x15, row.holiday?.x10, 1.5, 1);
    return { ...row, overtime_won: ot, night_won: ni, holiday_won: ho };
  };

  // 지급합계 K — 간이세액표 조회(소득세)와 일용직 보험료의 기준이 되는 값.
  //
  // 현장 소속: 기본급(시급×소정시간) + 시간외 + 야간 + 휴일 + 반장 + 연차
  // 본사(월급제): 기본급 + 특정업무수당/기술수당 + 직책수당 + 시간외수당 + 가족수당 + 연차수당
  //   → 본사 급여내역서 양식의 J열 =SUM(D9:I9)과 같은 구성.
  //   본사는 기본급이 시급×소정시간으로 안 나오므로(실제 5,206,420원은 209로
  //   나누어떨어지지 않는다) 정액으로 입력받은 base_pay를 쓴다.
  //   ⚠️ 정액급식비(meal_allow)는 여기 넣지 않는다 — 비공제 항목이라 실지급액에서만
  //   더한다(=K-R+S). K에 섞이면 간이세액 조회값과 보험료가 전부 틀어진다.
  const calcK = (row) => {
    if (isHeadOffice(row)) {
      return num(row.base_pay) + num(row.special_allow) + num(row.position_allow)
        + num(row.overtime_allow) + num(row.family_allow) + num(row.annual_allow);
    }
    const basic = Math.floor((num(row.hourly) * (num(row.work_hours) || 209)) / 10) * 10;
    return basic + num(row.overtime_won) + num(row.night_won) + num(row.holiday_won)
      + num(row.leader_allow) + num(row.annual_allow);
  };




  // 모달에서 주별값 변경 → 해당 행 JSON 갱신 + 금액 재계산
  const setWeek = (cat, sub, wIdx, val) => {
    setRows((p) => p.map((r, idx) => {
      if (idx !== modalIdx) return r;
      const block = { ...(r[cat] || {}) };
      const arr = Array.isArray(block[sub]) ? [...block[sub]] : [];
      arr[wIdx] = val;
      block[sub] = arr;
      return recalcRow({ ...r, [cat]: block });
    }));
  };


  // ── 엑셀 다운로드 (현재 보이는 행, 원본 레이아웃·함수) ──
  const exportExcel = async () => {
    if (visibleRows.length === 0) { setMsg("내보낼 행이 없어요."); return; }
    setBusy(true);
    try {
      const site = filter === "전체" ? "전체" : filter;
      const res = await api.post(
        "/payroll/sheet/export/",
        { year_month: ym, rows: visibleRows, worksite: site },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `[ERP] ${fmtYm(ym)} 급여명세서 (${site}).xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setMsg(`${fmtYm(ym)} 급여명세서 (${site}) 다운로드 완료.`);
    } catch (err) {
      setMsg("엑셀 다운로드 실패: " + (err.response?.data?.detail || err.message));
    } finally { setBusy(false); }
  };




  // ── insurance 명단 불러오기 ──
  const importRoster = async (t = ym) => {
    if (!t) { setMsg("연월을 먼저 선택하세요."); return; }
    setBusy(true);
    try {
      const r = await api.get("/payroll/roster/", { params: { ym: t } });
      let list = (r.data?.rows || []).map((x, i) => ({ ...emptyRow(i + 1), ...x }));
      if (list.length === 0) {
        setRows([]);   // 이전 달 잔상 제거
        setMsg(`${fmtYm(t)} 사회보험 명단이 없어요. 사회보험관리에서 먼저 등록하세요.`);
        setBusy(false); return;
      }

      // ✅ insurance 순서(name_order) 반영 — 인사이동자는 명단의 '아래쪽(현재 소속)' 위치(lastIndexOf)
      try {
        const ro = await api.get("/insurance/name-order/", { params: { ym: t } });
        const order = (Array.isArray(ro.data) ? ro.data : (ro.data?.names || []))
          .map((s) => String(s ?? "").replace(/\s+/g, "").trim());
        if (order.length) {
          const oi = (name) => {
            const i = order.lastIndexOf(String(name ?? "").replace(/\s+/g, "").trim());
            return i === -1 ? Number.MAX_SAFE_INTEGER : i;
          };
          list = list
            .slice()
            .sort((a, b) => {
              const d = oi(a.name) - oi(b.name);
              return d !== 0 ? d : String(a.name).localeCompare(String(b.name), "ko");
            })
            .map((x, i) => ({ ...x, seq: i + 1 }));   // 정렬 후 NO 재부여
        }
      } catch { /* 순서 없으면 원래 순서 유지 */ }

      setRows(list);
      setMsg(`${fmtYm(t)} 사회보험 명단 ${list.length}명 자동 연동됨. [개인별 매핑]으로 시급등급·소정시간·반장수당을 채우세요.`);
    } catch (err) { setMsg("명단 불러오기 실패: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };


  // ── 매핑 불러오기 (특정 월 지정 가능, 생략 시 전월) ──
  const importPrevMapping = async (srcYm) => {
    if (!ym || rows.length === 0) { setMsg("먼저 명단을 불러오세요."); return; }
    let prev = srcYm;
    if (!prev) {
      const [y, m] = ym.split("-").map(Number);
      prev = m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`;
    }
    setBusy(true);
    


    try {
      const r = await api.get("/payroll/sheet/prev-mapping/", { params: { ym: prev } });
      const src = {};
      (Array.isArray(r.data) ? r.data : []).forEach((p) => { src[pkey(p.name, p.birth)] = p; });
      let hit = 0;

      setRows((prevRows) => prevRows.map((row) => {
        const s = src[pkey(row.name, row.birth)];
        if (!s) return row;   // 매칭 안 되는 신규자는 그대로
        hit++;
        // 선택한 달 값으로 매핑 7종 덮어쓰기
        // ✅ meal_allow(정액급식비)는 매달 같은 정액이라 여기 포함. 단 본사 행에만
        // 적용한다 — 타 소속 행은 입력 자체가 비활성이라 값이 들어가면 안 된다.
        return {
          ...row,
          job: s.job ?? row.job,
          wage_grade: s.wage_grade ?? row.wage_grade,
          hourly: s.hourly ?? row.hourly,
          work_hours: s.work_hours ?? row.work_hours,
          leader_allow: s.leader_allow ?? row.leader_allow,
          annual_allow: s.annual_allow ?? row.annual_allow,
          meal_allow: isHeadOffice(row) ? (s.meal_allow ?? row.meal_allow) : 0,
          // ✅ 감면율·만료월도 사람 단위로 몇 년씩 유지되는 값이라 같이 복사한다
          // (감면 대상 소속 행만 — TAX_RELIEF_WORKSITES). 만료월을 함께 복사해야
          // 이번 달이 기간을 넘겼는지 스스로 판단할 수 있다 — 만료된 사람은 값이
          // 남아 있어도 reliefRateFor가 0%로 처리하고 "만료"로 표시한다.
          // ⚠️ 판정 기준은 불러오는 원본 달(prev)이 아니라 지금 편집 중인 달(ym)이다.
          // 종합환경은 7월엔 대상이 아니지만 8월엔 대상이므로, prev 기준으로 보면
          // 8월에 값을 못 받아온다.
          tax_relief_rate: isReliefWorksite(row, ym) ? (s.tax_relief_rate ?? row.tax_relief_rate) : 0,
          tax_relief_until: isReliefWorksite(row, ym) ? (s.tax_relief_until ?? row.tax_relief_until) : "",
          // ✅ 본사 월급제 항목 — 기본급·정액수당은 매달 거의 그대로라 같이 복사한다
          // (호봉·연봉 조정이 있던 달만 복사 후 고치면 된다).
          base_pay: isHeadOffice(row) ? (s.base_pay ?? row.base_pay) : 0,
          special_allow: isHeadOffice(row) ? (s.special_allow ?? row.special_allow) : 0,
          position_allow: isHeadOffice(row) ? (s.position_allow ?? row.position_allow) : 0,
          overtime_allow: isHeadOffice(row) ? (s.overtime_allow ?? row.overtime_allow) : 0,
          family_allow: isHeadOffice(row) ? (s.family_allow ?? row.family_allow) : 0,
        };
      }));



      
      if (hit) setPickedMap(prev);
      setMsg(hit ? `${fmtYm(prev)} 매핑 ${hit}명 반영. 신규자는 직접 채우세요.` : `${fmtYm(prev)} 매핑이 없어요.`);


    } catch (err) { setMsg("전월 매핑 실패: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  // ── 행 편집 ──
  const setCell = (i, key, val) => setRows((p) => p.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  const setGrade = (i, grade) => setRows((p) => p.map((r, idx) =>
    idx === i ? { ...r, wage_grade: grade, hourly: gradeHourly(grade) } : r));
  const addRow = () => setRows((p) => [...p, emptyRow(p.length + 1)]);
  const removeRow = (i) => setRows((p) => p.filter((_, idx) => idx !== i).map((r, idx) => ({ ...r, seq: idx + 1 })));

  // ── 데이터 완료/해제 토글 ──
  const toggleLock = async () => {
    if (!ym) return;
    if (rows.length === 0) { setMsg("저장된 데이터가 없어요."); return; }
    const next = !locked;
    if (next && !window.confirm(`${fmtYm(ym)} 데이터를 완료(잠금) 처리할까요?\n완료 상태에서는 수정·삭제가 막힙니다.`)) return;
    setBusy(true);
    try {
      const res = await api.post("/payroll/sheet/lock/", { ym, worksite: "", locked: next });
      setLocked(!!res.data?.locked);
      setMsg(next ? `${fmtYm(ym)} 데이터 완료 처리했습니다.` : `${fmtYm(ym)} 완료 해제했습니다.`);

    } catch (err) { setMsg("완료 처리 실패: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  // 간이세액표 조회 결과 → 그 행에 실제로 저장할 소득세·지방세.
  // 감면 대상이면 감면율만큼 깎고 지방세도 감면액 기준으로 다시 계산한다.
  // ⚠️ 감면율 0일 때는 조회값을 그대로 쓴다 — 감면 수식을 태우면 간이세액이 10원
  // 배수가 아닌 구간에서 원 단위가 깎여 감면과 무관한 기존 행의 세액이 바뀐다.
  const taxFromLookup = (data, row) => {
    const base = num(data?.income_tax);
    const rate = reliefRateFor(row, ym);
    return rate > 0
      ? applyTaxRelief(base, rate)
      : { income_tax: base, local_tax: num(data?.local_tax) };
  };

  // toggleLock 함수 다음에 추가
  const finishEdit = async () => {
    setEditIdx(null);
    setBusy(true);
    try {
      const next = await Promise.all(rows.map(async (row) => {
        const r2 = recalcRow(row);
        if (r2.worker_type === "일용") return { ...r2, income_tax: 0, local_tax: 0, tax_done: true };
        const k = calcK(r2);
        try {
          const res = await api.get("/payroll/tax-table/lookup/", { params: { salary: k } });
          return { ...r2, ...taxFromLookup(res.data, r2), tax_done: true };
        } catch { return { ...r2, tax_done: true }; }
      }));
      setRows(next);
      await save(next);
    } finally { setBusy(false); }
  };

  // 행 하나만 소득세·지방세 계산
  const calcTaxOne = async (i) => {
    setBusy(true);
    try {
      const row = rows[i];
      const r2 = recalcRow(row);
      if (r2.worker_type === "일용") {
        setRows((p) => p.map((r, idx) => idx === i ? { ...r2, income_tax: 0, local_tax: 0, tax_done: true } : r));
        return;
      }
      const k = calcK(r2);
      const res = await api.get("/payroll/tax-table/lookup/", { params: { salary: k } });
      const tax = taxFromLookup(res.data, r2);
      setRows((p) => p.map((r, idx) => idx === i ? { ...r2, ...tax, tax_done: true } : r));
    } catch (err) {
      setMsg("세금 계산 실패: " + (err.response?.data?.detail || err.message));
    } finally { setBusy(false); }
  };


  // ── 저장 ──
  const save = async (rowsToSave = rows) => {
    if (!ym) { setMsg("연월을 먼저 선택하세요."); return; }
    if (rowsToSave.length === 0) { setMsg("저장할 행이 없어요."); return; }

    // ✅ 감면율만 있고 만료월이 비어 있으면 경고 — 이 상태로 두면 감면이 무기한
    // 적용돼 기간이 끝난 뒤에도 계속 깎인다(과소 원천징수). 막지는 않고 확인만 받는다.
    const noUntil = rowsToSave.filter(
      (r) => isReliefWorksite(r, ym) && num(r.tax_relief_rate) > 0 && !String(r.tax_relief_until || "").trim()
    );
    if (noUntil.length) {
      const ok = window.confirm(
        `소득세 감면율은 있는데 감면 만료월이 비어 있는 행이 ${noUntil.length}건 있습니다.\n` +
        `(${noUntil.map((r) => r.name || "이름없음").join(", ")})\n\n` +
        `만료월이 없으면 기간이 끝난 뒤에도 감면이 계속 적용됩니다.\n이대로 저장할까요?`
      );
      if (!ok) { setMsg("저장을 취소했습니다. 감면 만료월을 입력하세요."); return; }
    }

    setBusy(true);
    try {


      const payload = rowsToSave.map((r) => ({
        ...r,
        seq: num(r.seq), hourly: num(r.hourly), work_hours: num(r.work_hours),
        leader_allow: num(r.leader_allow), annual_allow: num(r.annual_allow),
        // ✅ 정액급식비 — 본사 외 소속은 입력칸이 비활성이라 항상 0으로 나간다.
        meal_allow: isHeadOffice(r) ? num(r.meal_allow) : 0,
        // ✅ 감면 근거값 — 대상 소속·대상 월이 아니면 입력칸이 비활성이라 항상 0/빈 값으로 나간다.
        tax_relief_rate: isReliefWorksite(r, ym) ? num(r.tax_relief_rate) : 0,
        tax_relief_until: isReliefWorksite(r, ym) ? String(r.tax_relief_until || "").trim() : "",
        // ✅ 본사 월급제 항목 — 현장 소속은 기본급을 시급×소정시간으로 잡으므로
        // base_pay가 들어가면 지급합계가 이중으로 잡힌다. 항상 0으로 보낸다.
        base_pay: isHeadOffice(r) ? num(r.base_pay) : 0,
        special_allow: isHeadOffice(r) ? num(r.special_allow) : 0,
        position_allow: isHeadOffice(r) ? num(r.position_allow) : 0,
        overtime_allow: isHeadOffice(r) ? num(r.overtime_allow) : 0,
        family_allow: isHeadOffice(r) ? num(r.family_allow) : 0,
        health: num(r.health), care: num(r.care), pension: num(r.pension), employ: num(r.employ),
        employer: num(r.employer), injury: num(r.injury),
        income_tax: num(r.income_tax), local_tax: num(r.local_tax), work_days: num(r.work_days),
        tax_done: true,
      }));



      
      const res = await api.post("/payroll/sheet/save/", { year_month: ym, worksite: "", rows: payload });
      setMsg(`${fmtYm(ym)} 명세서 ${res.data?.saved}명 저장 완료.`);
      await loadMonths();


    } catch (err) { setMsg("저장 실패: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  const removeSheet = async () => {
    if (!ym) return;
    // ✅ 완료(잠금)된 달은 삭제도 막는다. 저장은 막으면서 "이 달 전체 삭제"만
    // 열려 있으면 잠금이 아무 의미가 없다 — 저장은 행 교체지만 이쪽은 시트째
    // 지우는 더 큰 작업이라 오히려 더 세게 막혀야 한다.
    // (버튼도 disabled지만, 상태가 어긋나는 경우를 위해 여기서도 본다)
    if (locked) {
      window.alert(
        `${fmtYm(ym)} 데이터가 완료 처리되어 있습니다.\n` +
        `삭제하려면 먼저 [데이터 해제하기]를 눌러 완료를 해제하세요.`
      );
      return;
    }
    if (!window.confirm(`${fmtYm(ym)} 명세서를 삭제할까요?\n(되돌릴 수 없습니다)`)) return;
    setBusy(true);
    try {
      await api.post("/payroll/sheet/delete/", { ym });
      setRows([]); setMsg(`${fmtYm(ym)} 명세서 삭제 완료.`);
      await loadMonths();
    } catch (err) { setMsg("삭제 실패: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  const S = {
    wrap: { padding: 16, fontSize: 13, color: "#1f2937" },
    bar: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 },
    btn: (c, d) => ({ padding: "6px 12px", background: d ? "#9ca3af" : c, color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, cursor: d ? "not-allowed" : "pointer", fontSize: 12, whiteSpace: "nowrap" }),
    btnSm: (c, d) => ({ width: "auto", flex: "0 0 auto", display: "inline-block", padding: "4px 10px", background: d ? "#9ca3af" : c, color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, cursor: d ? "not-allowed" : "pointer", fontSize: 12, whiteSpace: "nowrap" }),

    sel: { padding: "3px 6px", borderRadius: 4, border: "1px solid #cbd5e1" },
    chip: (a) => ({ padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 12, border: a ? "1px solid #2563eb" : "1px solid #d1d5db", background: a ? "#2563eb" : "#fff", color: a ? "#fff" : "#374151" }),
    th: { background: "#f1f5f9", padding: "5px 6px", borderBottom: "1px solid #cbd5e1", whiteSpace: "nowrap", fontSize: 12 },
    // ⚠️ nowrap은 th·td 양쪽에 있어야 한다. 이 표는 tableLayout: auto라 컬럼 폭이
    // 내용에서 나오는데, 20열이 넘어 컨테이너보다 넓어지면 브라우저가 "꺾일 수 있는"
    // 셀부터 압축한다 — th만 nowrap이던 탓에 3자 성명("서복성")이 "서복"+"성"으로
    // 꺾였다. td까지 nowrap이면 표의 min-content 폭이 커지고, 넘치는 만큼은 감싼
    // div(overflowX: auto)가 가로 스크롤로 처리한다(= 텍스트를 꺾지 않는다).
    td: { padding: "3px 4px", borderBottom: "1px solid #eef2f7", whiteSpace: "nowrap" },


    // 소득세 셀 뱃지 — 감면 적용중(보라) / 기간 만료(회색). 좁은 칸이라 nowrap 고정.
    badge: (c, bg) => ({ padding: "0 4px", borderRadius: 3, fontSize: 10, fontWeight: 700, color: c, background: bg, whiteSpace: "nowrap" }),
    inp: { width: "100%", padding: "3px 5px", borderRadius: 4, border: "1px solid #cbd5e1", boxSizing: "border-box", fontSize: 12 },
    inpR: { width: "100%", padding: "3px 5px", borderRadius: 4, border: "1px solid #cbd5e1", boxSizing: "border-box", fontSize: 12, textAlign: "right" },

    // ✅ 비고 — S.td의 nowrap에서 유일하게 폭 상한을 두는 칸(견적서 목록 변경사항 셀과 같은 패턴).
    // 비고만 자유 입력 100자(모델 max_length)라, nowrap만 걸면 긴 비고 한 건이 이 컬럼을
    // 그대로 밀어내 표 전체가 옆으로 늘어난다 — 100자 비고 1건 실측으로 컬럼 1,608px,
    // 표 3,695px까지 갔다(상한 적용 후 188px / 2,275px).
    // maxWidth로 max-content 기여를 잘라 컬럼 폭을 고정하고, 잘린 뒷부분은 말줄임 +
    // title 툴팁으로 살린다 — 꺾지도 않고 값을 감추지도 않는다.
    // ⚠️ td에 직접 걸면 안 된다. tableLayout: auto에서 td의 max-width는 브라우저가
    // 무시할 수 있어 안쪽 div에 건다(견적서 ChangeNoteCell과 같은 이유).
    noteCell: { maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  };



  const numCell = (i, key, w = 70, allowDecimal = false) => (
    <td style={S.td}>
      {editIdx === i
        ? <input
            value={allowDecimal
              ? (rows[i][key] ?? "")                                  // 소수칸: 입력 그대로 (209.5 가능)
              : (rows[i][key] ? fmt(num(rows[i][key])) : "")}
            onChange={(e) => setCell(i, key, e.target.value)}
            style={{ ...S.inpR, width: w }} placeholder="0" />
        : <div style={{ width: w, textAlign: "right", color: "#1f2937" }}>
            {rows[i][key] ? (allowDecimal ? num(rows[i][key]) : fmt(num(rows[i][key]))) : "-"}
          </div>}
    </td>
  );


  // ══════════════════════════════════════════════════════════════════
  // 표 컬럼 정의 — 기존 하드코딩 <th>/<td> 나열을 그대로 옮겨 담은 것.
  // 이 배열 하나가 헤더·본문·빈줄 colSpan의 단일 출처(single source)다.
  //
  // headers: 이 컬럼이 차지하는 헤더 칸(보통 1개). "시간외/야간/휴일"만
  //   헤더 3칸 ↔ 본문 1칸(colSpan=3) 구조라 배열로 둔다.
  // render : 그 행의 <td>를 반환. 기존 JSX를 그대로 옮겨 동작이 같다.
  // visible: 소속별 노출 분기. 미지정이면 항상 표시.
  //
  // ⚠️ 숨기기는 "소속 필터로 좁혀본 화면"에서만 한다(headOfficeView). "전체"는 여러
  // 소속 행이 한 표에 섞이므로, 한 소속 기준으로 열을 빼면 다른 소속 행의 값이
  // 화면에서 사라진다. 또 숨김은 표시만 끄는 것이지 값을 지우지 않는다 —
  // leader_allow는 baseHourly()의 통상임금과 calcK()의 지급합계에 그대로 들어가므로,
  // 값이 있는 컬럼을 숨기면 "안 보이는데 세액에는 반영되는" 상태가 된다.
  // 그래서 실제 저장 데이터가 전부 0인 컬럼만 숨긴다(본사 18행 반장수당 전부 0 실측).
  // ══════════════════════════════════════════════════════════════════
  // ✅ 본사 소속만 골라 본 화면인지 — 본사 급여내역서 양식에 없는 열을 숨기는 기준.
  const headOfficeView = filter === HEAD_OFFICE;
  const ALL_COLUMNS = [
    { key: "seq", headers: ["NO"],
      render: (r) => <td style={{ ...S.td, textAlign: "center", color: "#94a3b8" }}>{r.seq}</td> },

    { key: "worksite", headers: ["근무지"],
      render: (r, i, ed) => <td style={S.td}>{ed ? <input value={r.worksite} onChange={(e) => setCell(i, "worksite", e.target.value)} style={{ ...S.inp, width: 130 }} /> : <span>{r.worksite || "-"}</span>}</td> },

    { key: "job", headers: ["직무"],
      render: (r, i, ed) => <td style={S.td}>{ed ? <input value={r.job} onChange={(e) => setCell(i, "job", e.target.value)} style={{ ...S.inp, width: 70 }} /> : <span>{r.job || "-"}</span>}</td> },

    // ✅ 성명 — 한글 4자(외자~4자 성명)가 한 줄에 들어가는 최소폭을 보장한다.
    // 13px 기준 한글 1자 ≈ 13px이라 4자 52px + td 좌우 padding 8px = 60px.
    { key: "name", headers: ["성명"],
      render: (r, i, ed) => <td style={{ ...S.td, minWidth: 60 }}>{ed ? <input value={r.name} onChange={(e) => setCell(i, "name", e.target.value)} style={{ ...S.inp, width: 70 }} /> : <span>{r.name || "-"}</span>}</td> },

    { key: "birth", headers: ["생년월일"],
      render: (r, i, ed) => <td style={S.td}>{ed ? <input value={r.birth} onChange={(e) => setCell(i, "birth", e.target.value)} style={{ ...S.inp, width: 64 }} /> : <span>{r.birth || "-"}</span>}</td> },

    { key: "worker_type", headers: ["구분"],
      render: (r, i, ed) => (
        <td style={S.td}>
          {ed
            ? <select value={r.worker_type} onChange={(e) => setCell(i, "worker_type", e.target.value)} style={{ ...S.sel, width: 60 }}>
                <option value="일반">일반</option><option value="일용">일용</option>
              </select>
            : <span style={r.worker_type === "일용" ? { color: "#b45309", fontWeight: 800 } : {}}>{r.worker_type}</span>}
        </td>
      ) },

    // ✅ 시급등급·시급은 시급제(현장 소속) 전용 — 본사는 월급제라 기본급을 정액으로
    // 입력받고 calcK 본사 분기가 시급을 쓰지 않는다(실제 본사 행도 전부 시급 0).
    // 숨겨도 값에는 영향이 없다: 본사 행은 시간외/야간/휴일 입력이 없어 시급을
    // 참조하는 baseHourly()·recalcRow()가 늘 0을 돌려준다.
    { key: "wage_grade", headers: ["시급등급"], visible: () => !headOfficeView,
      render: (r, i, ed) => (
        <td style={S.td}>
          {ed
            ? <select value={r.wage_grade} onChange={(e) => setGrade(i, e.target.value)} style={{ ...S.sel, width: 170 }}>
                <option value="">선택</option>
                {grades.map((g) => <option key={g.grade} value={g.grade}>{g.grade}</option>)}
              </select>
            : <span>{r.wage_grade || "-"}</span>}
        </td>
      ) },

    { key: "work_hours", headers: ["소정시간"],
      render: (r, i, ed) => (
        <td style={{ ...S.td, background: (!ed && num(r.work_hours) !== 209) ? "#fef08a" : undefined }}>
          {ed
            ? <input
                value={r.work_hours ?? ""}
                onChange={(e) => setCell(i, "work_hours", e.target.value)}
                style={{ ...S.inpR, width: 56 }} placeholder="0" />
            : <div style={{
                width: 56, textAlign: "right",
                color: num(r.work_hours) !== 209 ? "#b45309" : "#1f2937",
                fontWeight: num(r.work_hours) !== 209 ? 800 : 400,
              }}>
                {r.work_hours ? num(r.work_hours) : "-"}
              </div>}
        </td>
      ) },

    { key: "hourly", headers: ["시급"], visible: () => !headOfficeView,
      render: (r) => <td style={{ ...S.td, textAlign: "right", color: "#64748b" }}>{fmt(r.hourly)}</td> },

    // ══════════════════════════════════════════════════════════════
    // 본사(월급제) 전용 급여 항목 — 본사 급여내역서 양식 D~H열 순서 그대로.
    // 본사 필터에서만 보이고, 이 때 시간외/야간/휴일 3종과 반장수당은 숨는다
    // (시간외는 아래 정액 "시간외수당" 칸으로 대체 — 양식 G열이 금액 직접 입력이다).
    // ══════════════════════════════════════════════════════════════
    { key: "base_pay", headers: ["기본급"], visible: () => headOfficeView,
      render: (r, i) => numCell(i, "base_pay", 90) },
    // 양식 E8이 "특정업무수당/기술수당" 한 칸이라 여기서도 한 컬럼으로 둔다.
    { key: "special_allow", headers: ["특정업무수당/기술수당"], visible: () => headOfficeView,
      render: (r, i) => numCell(i, "special_allow", 90) },
    { key: "position_allow", headers: ["직책수당"], visible: () => headOfficeView,
      render: (r, i) => numCell(i, "position_allow", 80) },
    { key: "overtime_allow", headers: ["시간외수당"], visible: () => headOfficeView,
      render: (r, i) => numCell(i, "overtime_allow", 80) },
    { key: "family_allow", headers: ["가족수당"], visible: () => headOfficeView,
      render: (r, i) => numCell(i, "family_allow", 80) },

    // ⚠️ 헤더 3칸 ↔ 본문 1칸(colSpan=3). 클릭하면 수당입력 모달이 열린다.
    { key: "allowance", headers: ["시간외", "야간", "휴일"], visible: () => !headOfficeView,
      render: (r, i, ed) => (
        <td colSpan={3} title={ed ? "클릭해서 수당 입력" : "수정 버튼을 눌러야 입력 가능"} onClick={() => { if (ed) setModalIdx(i); }}
          style={{ ...S.td, cursor: ed ? "pointer" : "default", background: "#f8fafc" }}>
          {(r.overtime_won || r.night_won || r.holiday_won) ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, textAlign: "right" }}>
              <span>{r.overtime_won ? fmt(r.overtime_won) : "-"}</span>
              <span>{r.night_won ? fmt(r.night_won) : "-"}</span>
              <span>{r.holiday_won ? fmt(r.holiday_won) : "-"}</span>
            </div>
          ) : (
            <div style={{ textAlign: "center", color: "#2563eb", fontWeight: 600 }}>수당입력</div>
          )}
        </td>
      ) },

    // ✅ 반장수당 — 본사 급여내역서 양식(A~S)에 아예 없는 항목이고, 저장된 본사
    // 18행도 전부 0이라 본사 화면에서만 숨긴다. 현장 소속(금나래·안양천 등)은
    // 실제로 쓰고 있으므로 "전체"와 그 소속 화면에서는 그대로 보인다.
    { key: "leader_allow", headers: ["반장수당"], visible: () => !headOfficeView,
      render: (r, i) => numCell(i, "leader_allow", 80) },
    { key: "annual_allow", headers: ["연차수당"], render: (r, i) => numCell(i, "annual_allow", 70) },

    // ✅ 감면율 / 감면만료 — 중소기업 취업자 소득세 감면(조특법 §30). 대상 소속·대상
    // 월(TAX_RELIEF_WORKSITES)만 입력 활성, 나머지는 회색 비활성으로 열 정렬만 유지한다.
    // 종합환경 행은 같은 사람이라도 2026-07 화면에서는 비활성, 2026-08부터 활성이다.
    { key: "tax_relief_rate", headers: ["감면율"],
      render: (r, i, ed) => (!isReliefWorksite(r, ym)
        ? <td style={{ ...S.td, textAlign: "center", background: "#f8fafc", color: "#cbd5e1" }} title={reliefOffReason(r)}>-</td>
        : (
          <td style={{ ...S.td, textAlign: "center", whiteSpace: "nowrap" }}>
            {ed
              ? <select value={num(r.tax_relief_rate)} onChange={(e) => setCell(i, "tax_relief_rate", num(e.target.value))} style={{ ...S.sel, width: 76 }}>
                  {TAX_RELIEF_RATES.map((v) => <option key={v} value={v}>{v === 0 ? "미적용" : `${v}%`}</option>)}
                </select>
              : <span style={num(r.tax_relief_rate) > 0 ? { color: "#7c3aed", fontWeight: 700 } : { color: "#cbd5e1" }}>
                  {num(r.tax_relief_rate) > 0 ? `${num(r.tax_relief_rate)}%` : "-"}
                </span>}
          </td>
        )) },

    { key: "tax_relief_until", headers: ["감면만료"],
      render: (r, i, ed) => (!isReliefWorksite(r, ym)
        ? <td style={{ ...S.td, textAlign: "center", background: "#f8fafc", color: "#cbd5e1" }} title={reliefOffReason(r)}>-</td>
        : (
          <td style={{ ...S.td, textAlign: "center", whiteSpace: "nowrap" }}>
            {ed
              ? <input type="month" value={String(r.tax_relief_until || "")} onChange={(e) => setCell(i, "tax_relief_until", e.target.value)}
                  title="감면 만료월 — 이 달까지 감면 적용" style={{ ...S.inp, width: 120 }} />
              : <span style={isReliefExpired(r, ym) ? { color: "#dc2626", fontWeight: 700 } : { color: "#64748b" }}>
                  {r.tax_relief_until || (num(r.tax_relief_rate) > 0 ? "기한없음" : "-")}
                </span>}
          </td>
        )) },

    { key: "income_tax", headers: ["소득세"],
      render: (r, i) => (
        <td style={{ ...S.td, textAlign: "right" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
            {/* ✅ 감면 뱃지 — 이번 급여월에 실제로 적용된 감면율. 감면율이 있어도
                만료월을 지났으면 0%가 적용되므로 "만료"로 구분해 보여준다. */}
            {reliefRateFor(r, ym) > 0 && (
              <span style={S.badge("#6d28d9", "#ede9fe")} title="중소기업 취업자 소득세 감면(조특법 §30) 적용">
                감면{reliefRateFor(r, ym)}%
              </span>
            )}
            {isReliefExpired(r, ym) && (
              <span style={S.badge("#b91c1c", "#fee2e2")} title={`감면기간 만료(${r.tax_relief_until}까지) — 이번 달은 감면 없이 계산합니다`}>
                만료
              </span>
            )}
            <span style={{ color: r.tax_done ? "#1f2937" : "#cbd5e1" }}>{r.tax_done ? fmt(r.income_tax) : "-"}</span>
            <button
              title="소득세·지방세 계산"
              disabled={busy}
              onClick={() => calcTaxOne(i)}
              style={{ padding: "1px 6px", fontSize: 10, borderRadius: 4, border: "1px solid #93c5fd", background: "#eff6ff", color: "#2563eb", cursor: busy ? "not-allowed" : "pointer" }}
            >계산</button>
          </div>
        </td>
      ) },

    { key: "local_tax", headers: ["지방세"],
      render: (r) => <td style={{ ...S.td, textAlign: "right", color: r.tax_done ? "#1f2937" : "#cbd5e1" }}>{r.tax_done ? fmt(r.local_tax) : "-"}</td> },

    { key: "health", headers: ["건강"], render: (r, i) => numCell(i, "health", 70) },
    { key: "care", headers: ["요양"], render: (r, i) => numCell(i, "care", 64) },
    { key: "pension", headers: ["국민"], render: (r, i) => numCell(i, "pension", 70) },
    { key: "employ", headers: ["고용"], render: (r, i) => numCell(i, "employ", 70) },
    { key: "employer", headers: ["사업주"], render: (r, i) => numCell(i, "employer", 64) },
    { key: "injury", headers: ["산재"], render: (r, i) => numCell(i, "injury", 64) },

    // ✅ 정액급식비 — 비공제(비과세성 지급). 본사 소속 행에만 입력 활성.
    // 타 소속은 회색 비활성으로 두어 열 정렬은 유지한다(행마다 셀을 빼면
    // 표 전체가 어긋난다).
    { key: "meal_allow", headers: ["정액급식비"],
      render: (r, i) => (isHeadOffice(r)
        ? numCell(i, "meal_allow", 80)
        : <td style={{ ...S.td, textAlign: "right", background: "#f8fafc", color: "#cbd5e1" }} title="본사 소속만 입력합니다">-</td>) },

    // ✅ 비고 — 폭 상한(S.noteCell) + 말줄임 + title 툴팁. 이 표에서 유일하게 길이를
    // 예측할 수 없는 자유 입력칸이라, 넘치는 만큼 표를 미는 대신 여기서 끊는다.
    { key: "note", headers: ["비고"],
      render: (r, i, ed) => <td style={S.td}>{ed ? <input value={r.note} onChange={(e) => setCell(i, "note", e.target.value)} style={{ ...S.inp, width: 90 }} /> : <div style={S.noteCell} title={r.note || ""}>{r.note || "-"}</div>}</td> },

    // ✅ 연차/공가/근무일수/실근무시간 — 매달 새로 입력하는 값(전월 매핑 불러오기가
    // 복사하지 않는 4종), 폭 좁게(60px)+줄바꿈 금지. 연차·공가는 0.5일 단위,
    // 실근무시간은 소수 둘째자리까지 허용(inputMode=decimal, 숫자 검증은 저장 시
    // 백엔드가 처리).
    { key: "annual_leave_days", headers: ["연차"],
      render: (r, i, ed) => <td style={{ ...S.td, whiteSpace: "nowrap" }}>{ed ? <input inputMode="decimal" value={r.annual_leave_days ?? ""} onChange={(e) => setCell(i, "annual_leave_days", e.target.value)} style={{ ...S.inp, width: 60 }} /> : <span>{r.annual_leave_days || "-"}</span>}</td> },
    { key: "official_leave_days", headers: ["공가"],
      render: (r, i, ed) => <td style={{ ...S.td, whiteSpace: "nowrap" }}>{ed ? <input inputMode="decimal" value={r.official_leave_days ?? ""} onChange={(e) => setCell(i, "official_leave_days", e.target.value)} style={{ ...S.inp, width: 60 }} /> : <span>{r.official_leave_days || "-"}</span>}</td> },
    { key: "work_days", headers: ["근무일수"],
      render: (r, i, ed) => <td style={{ ...S.td, whiteSpace: "nowrap" }}>{ed ? <input inputMode="numeric" value={r.work_days ?? ""} onChange={(e) => setCell(i, "work_days", e.target.value)} style={{ ...S.inp, width: 55 }} /> : <span>{r.work_days || "-"}</span>}</td> },
    { key: "actual_work_hours", headers: ["실근무시간"],
      render: (r, i, ed) => <td style={{ ...S.td, whiteSpace: "nowrap" }}>{ed ? <input inputMode="decimal" value={r.actual_work_hours ?? ""} onChange={(e) => setCell(i, "actual_work_hours", e.target.value)} style={{ ...S.inp, width: 65 }} /> : <span>{r.actual_work_hours || "-"}</span>}</td> },

    { key: "actions", headers: [""],
      render: (r, i, ed) => (
        <td style={S.td}>
          <div style={{ display: "flex", gap: 4 }}>
            {ed
              ? <button style={S.btnSm("#16a34a", busy)} disabled={busy} onClick={finishEdit}>완료</button>
              : <button style={S.btnSm("#f59e0b", busy)} disabled={busy} onClick={() => {
                  if (locked) { window.alert(`${fmtYm(ym)} 데이터가 완료 처리되어 있습니다.\n수정하려면 먼저 [데이터 해제하기]를 눌러 완료를 해제하세요.`); return; }
                  setEditIdx(i);
                }}>수정</button>}
            <button style={S.btnSm("#dc2626", busy)} disabled={busy} onClick={() => {
              if (locked) { window.alert(`${fmtYm(ym)} 데이터가 완료 처리되어 있습니다.\n삭제하려면 먼저 [데이터 해제하기]를 눌러 완료를 해제하세요.`); return; }
              if (window.confirm("이 행을 삭제할까요?")) removeRow(i);
            }}>삭제</button>
          </div>
        </td>
      ) },
  ];

  const COLUMNS = ALL_COLUMNS.filter((c) => (c.visible ? c.visible() : true));
  const HEADER_LABELS = COLUMNS.flatMap((c) => c.headers);

  return (
    <div style={S.wrap}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>급여명세서</h2>
      {/* ✅ 실제 흐름으로 교체 — 그전 문구는 화면에 없는 버튼 두 개
          ([insurance 명단 불러오기] · [전월 매핑 불러오기])를 안내하고 있었다.
          명단은 버튼이 아니라 연월 선택 시 자동으로 들어오고, 매핑 버튼의 실제
          라벨은 [{연월} 개인별 매핑]이다. 끝의 "(1단계: …엑셀은 다음 단계)"도
          주별 입력 모달·엑셀 다운로드가 이미 있어 사실과 달라 뺐다. */}
      <p style={{ color: "#6b7280", marginBottom: 14, lineHeight: 1.7 }}>
        <b>연월</b>을 고르면 그 달 사회보험 명단이 자동으로 들어옵니다
        (먼저 [사회보험 관리]에 그 달 자료가 등록돼 있어야 합니다).
        이어서 <b>[{ym ? `${fmtYm(ym)} ` : ""}개인별 매핑]</b>으로 시급등급·소정시간·반장수당을
        다른 달에서 가져오고, 행별 <b>[수정]</b>으로 수당·연차·근무일수를 채운 뒤
        <b>[완료]</b>(전체 세금 재계산 후 저장) 또는 <b>[저장]</b>을 누르세요.
        마무리되면 <b>[데이터 완료(잠금하기)]</b>로 그 달을 잠급니다.
        시급등급을 고르면 그 해 시급이 자동으로 들어갑니다.
      </p>


      {rows.length > 0 && (
        locked ? (
          <div style={{
            background: "#eff6ff", border: "2px solid #2563eb", padding: "10px 14px",
            borderRadius: 8, marginBottom: 12,
            color: "#000", fontSize: 18, fontWeight: 800,
          }}>🔒 {fmtYm(ym)} 데이터 완료 처리됨. (수정·삭제 잠금)</div>
        ) : (
          <div style={{
            background: "#fff7ed", border: "2px solid #f59e0b", padding: "10px 14px",
            borderRadius: 8, marginBottom: 12,
            color: "#000", fontSize: 18, fontWeight: 800,
          }}>⚠ {fmtYm(ym)} 아직 데이터 완료 처리 전입니다.</div>
        )
      )}

      {msg && (
        <div style={{
          background: "#f0f9ff", border: "1px solid #bae6fd", padding: "6px 10px",
          borderRadius: 6, marginBottom: 12, color: "#075985", fontSize: 13,
        }}>{msg}</div>
      )}




      <div style={S.bar}>
        <label>연월</label>
        <select value={ym.slice(0, 4)} onChange={(e) => setYm(`${e.target.value}-${ym.slice(5, 7) || "01"}`)} style={{ ...S.sel, width: 90 }}>
          <option value="">년</option>
          {YEARS.map((y) => <option key={y} value={y}>{y}년</option>)}
        </select>
        <select value={ym.slice(5, 7)} onChange={(e) => setYm(`${ym.slice(0, 4) || NOW.getFullYear()}-${e.target.value}`)} style={{ ...S.sel, width: 70 }}>
          <option value="">월</option>
          {MONTHS.map((m) => <option key={m} value={m}>{+m}월</option>)}
        </select>


        <button title="명세서 저장" style={S.btnSm("#16a34a", busy)} disabled={busy} onClick={() => save()}>저장</button>


        <button title="현재 소속 엑셀 다운로드" style={S.btnSm("#15803d", busy)} disabled={busy} onClick={exportExcel}>엑셀 다운로드</button>

        <button
          title="개인별 매핑 가져오기"
          style={S.btnSm(isMapped ? "#059669" : "#7c3aed", busy || rows.length === 0)}
          disabled={busy || rows.length === 0}
          onClick={() => { setPickedMap(""); setShowMapModal(true); }}
        >
          {ym ? `${fmtYm(ym)} 개인별 매핑${isMapped ? " 완료 ✓" : ""}` : "개인별 매핑"}
        </button>

        <button
          title={locked ? "데이터 완료 해제" : "데이터 완료(수정·삭제 잠금)"}
          style={S.btnSm(locked ? "#b45309" : "#2563eb", busy || rows.length === 0)}
          disabled={busy || rows.length === 0}
          onClick={toggleLock}
        >
          {locked ? `🔒 ${fmtYm(ym)} 데이터 해제하기(장금해제)` : `${fmtYm(ym)} 데이터 완료(잠금하기)`}


        </button>

        <button
          title={locked
            ? `${fmtYm(ym)} 데이터가 완료(잠금) 상태입니다 — 먼저 [데이터 해제하기]를 누르세요`
            : "이 달 명세서 삭제"}
          style={S.btnSm("#dc2626", busy || locked)}
          disabled={busy || locked}
          onClick={removeSheet}
        >이 달 매핑 데이터 삭제</button>




      </div>

      {grades.length === 0 && ym && (
        <div style={{ color: "#b45309", marginBottom: 8 }}>⚠ {year}년 시급등급이 없어요. [시급 설정]에서 먼저 등록하세요.</div>
      )}

      {rows.length > 0 && (
        <div style={{ ...S.bar, marginBottom: 8 }}>
          <span style={{ color: "#475569", fontWeight: 600 }}>소속:</span>
          <div
            style={S.chip(filter === "전체")}
            onClick={() => setFilter("전체")}
          >전체 {rows.length}</div>
          {sites.map((site) => (
            <div
              key={site}
              style={S.chip(filter === site)}
              onClick={() => setFilter(site)}
            >{site} {rows.filter((r) => r.worksite === site).length}</div>
          ))}
        </div>
      )}



      <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 8 }}>
        <table style={{ borderCollapse: "collapse", minWidth: 1400 }}>
          <thead>
            <tr>
              {HEADER_LABELS.map((label, hi) => <th key={hi} style={S.th}>{label}</th>)}
            </tr>
          </thead>
          <tbody>

            {visibleRows.length === 0 ? (
              <tr><td style={S.td} colSpan={HEADER_LABELS.length}>{rows.length === 0
                ? "명단이 없습니다. 위에서 연월을 고르면 그 달 사회보험 명단이 자동으로 들어옵니다. (그래도 비어 있으면 [사회보험 관리]에 그 달 자료가 없는 것입니다)"
                : "이 소속에 해당하는 인원이 없습니다."}</td></tr>

            ) : visibleRows.map((r) => {
              const i = rows.indexOf(r);   // ✅ 원본 인덱스 (편집·삭제용)
              const ed = editIdx === i;    // 이 행이 편집 중인지
              return (


                <tr key={i} style={ed ? { background: "#eff6ff" } : (r.worker_type === "일용" ? { background: "#fffbeb" } : {})}>


                  {COLUMNS.map((c) => (
                    <React.Fragment key={c.key}>{c.render(r, i, ed)}</React.Fragment>
                  ))}



                </tr>
              );
            })}
          </tbody>


        </table>
      </div>

      <div style={{ ...S.bar, marginTop: 10 }}>
        <button style={S.btnSm("#0891b2", busy)} disabled={busy} onClick={addRow}>+ 행 추가</button>
        {rows.length > 0 && <span style={{ color: "#94a3b8" }}>현재 {rows.length}명 (일용 {rows.filter((r) => r.worker_type === "일용").length}명)</span>}
      </div>

      {months.length > 0 && <p style={{ color: "#94a3b8", fontSize: 12, marginTop: 8 }}>저장된 명세서: {months.join(", ")}</p>}

      {/* ── 수당 입력 모달 ── */}
      {modalIdx !== null && rows[modalIdx] && (() => {
        const r = rows[modalIdx];
        const weeks = weeksOfMonth(ym);
        const base = baseHourly(r);
        const baseLabel = num(r.leader_allow) > 0
          ? `(기본급+반장)/${num(r.work_hours) || 209} = ${fmt(Math.round(base))}원`
          : `시급 ${fmt(num(r.hourly))}원`;
        const cell = (cat, sub, wIdx) => (
          <input
            value={(r[cat]?.[sub]?.[wIdx]) ?? ""}
            onChange={(e) => setWeek(cat, sub, wIdx, e.target.value)}
            style={{ width: 48, padding: "3px 4px", borderRadius: 4, border: "1px solid #cbd5e1", textAlign: "right", fontSize: 12 }}
            placeholder="0"
          />
        );
        const rowBlock = (title, cat, subs) => (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{title}</div>
            <table style={{ borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...S.th, textAlign: "left" }}>배율</th>
                  {weeks.map((w) => <th key={w.idx} style={S.th}>{w.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {subs.map(([sub, mlabel]) => (
                  <tr key={sub}>
                    <td style={{ ...S.td, fontWeight: 600, whiteSpace: "nowrap" }}>{mlabel}</td>
                    {weeks.map((w) => <td key={w.idx} style={S.td}>{cell(cat, sub, w.idx)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        return (
          <div {...allowBackdrop} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <div style={{ background: "#fff", borderRadius: 10, width: "min(820px, 96vw)", maxHeight: "88vh", overflow: "auto", padding: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <b style={{ fontSize: 15 }}>{r.name || "(이름)"} 수당 입력 — {fmtYm(ym)}</b>
                <button style={S.btnSm("#64748b", false)} onClick={() => setModalIdx(null)}>닫기 ✕</button>
              </div>
              <div style={{ color: "#64748b", marginBottom: 14 }}>단가 기준: {baseLabel} · 주별로 시간을 입력하세요.</div>

              {rowBlock("시간외수당", "overtime", [["x15", "1.5배"], ["x10", "1배"]])}
              {rowBlock("야간근로수당", "night", [["x05", "0.5배"]])}
              {rowBlock("휴일근로수당", "holiday", [["x15", "1.5배"], ["x10", "1배"]])}

              <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: 12, display: "flex", gap: 20, flexWrap: "wrap" }}>
                <span>시간외 <b>{fmt(r.overtime_won)}원</b></span>
                <span>야간 <b>{fmt(r.night_won)}원</b></span>
                <span>휴일 <b>{fmt(r.holiday_won)}원</b></span>
              </div>

              <div style={{ marginTop: 14, textAlign: "right" }}>
                <button style={S.btnSm("#16a34a", false)} onClick={() => setModalIdx(null)}>확인</button>
              </div>
            </div>
          </div>
        );
      })()}

{/* ── 개인별 매핑 설정 모달 ── */}
      {showMapModal && (
        <div {...mapBackdrop} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: "#fff", borderRadius: 10, width: "min(520px, 96vw)", padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <b style={{ fontSize: 15 }}>{fmtYm(ym)} 개인별 매핑 설정</b>
              <button style={S.btnSm("#64748b", false)} onClick={() => setShowMapModal(false)}>닫기 ✕</button>
            </div>
            <div style={{ color: "#64748b", marginBottom: 12, fontSize: 12 }}>
              저장된 다른 달 명세서에서 시급등급·소정시간·반장수당·연차수당·직무를 이름+생년월일로 매칭해 <b>덮어씁니다. 기존에 입력한 매핑값은 사라집니다.</b> 신규자(매칭 안 되는 사람)는 직접 입력하세요.

            </div>

            <select
              value={pickedMap}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                if (window.confirm(`${fmtYm(v)} 자료로 수정하시겠습니까?\n바꾸면 그전에 입력한 매핑 자료는 삭제됩니다.`)) {
                  importPrevMapping(v);
                }
                e.target.value = "";   // 셀렉트 초기화 (취소하든 적용하든 다시 고를 수 있게)
              }}
              disabled={busy || rows.length === 0}
              style={{ ...S.sel, width: "100%", padding: "8px 10px", fontSize: 14 }}
            >


              <option value="">📂 저장된 db 가져오기</option>
              {months.map((m) => (
                <option key={m} value={m}>{fmtYm(m)} 매핑{m === ym ? " (현재 달)" : ""}</option>
              ))}
            </select>
            {pickedMap && (
              <div style={{ marginTop: 12, color: "#16a34a", fontWeight: 600, fontSize: 13 }}>
                ✓ {fmtYm(pickedMap)} db 가져옴
              </div>
            )}
            <div style={{ marginTop: 18, textAlign: "right" }}>
              <button style={S.btnSm("#16a34a", false)} onClick={() => setShowMapModal(false)}>확인</button>
            </div>
          </div>
        </div>
      )}

      {/* ── 떠있는 저장 버튼 ── */}
      {rows.length > 0 && (
        <button
          onClick={() => save()}
          disabled={busy}
          title="저장 (Ctrl+S)"
          style={{
            position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 900,
            padding: "14px 22px",
            background: busy ? "#9ca3af" : "#16a34a",
            color: "#fff", border: "none",
            fontWeight: 700, fontSize: 15,
            boxShadow: "0 -2px 10px rgba(0,0,0,0.15)",
            cursor: busy ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}
        >
          💾 {busy ? "저장중…" : "저장"}
          <span style={{ fontSize: 11, fontWeight: 500, opacity: 0.85, marginLeft: 8 }}>
            [Ctrl + S 가능]
          </span>

        </button>
      )}
    </div>
  );
}
